import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { graphql } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { ButtonProps } from '@atlaskit/button/default/button';
import type { LinkButtonProps } from '@atlaskit/button/link';

import { WIKI } from '@confluence/named-routes/entry-points/routes.ts';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { useIsLicensed } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { fg } from '@confluence/feature-gating';
import { useLazyQueryPromiseInterop } from '@confluence/apollo-relay-interop/entry-points/useLazyQueryPromiseInterop.ts';
import { expVal } from '@confluence/feature-experiments';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';

import type { ConfluenceJoinabilityType } from './request-license/useIsAuthenticatedUnlicensedWithDirectAccess.ts';
import type {
	useRestrictedSpaceScreenButtonPropsResolvedSpaceKeyForContentQuery$data,
	useRestrictedSpaceScreenButtonPropsResolvedSpaceKeyForContentQuery$variables,
} from './__generated__/useRestrictedSpaceScreenButtonPropsResolvedSpaceKeyForContentQuery.graphql.ts';

interface UseButtonPropsReturn {
	primary: ButtonProps | LinkButtonProps;
	secondary?: ButtonProps | LinkButtonProps;
}

interface UseButtonParams {
	confluenceJoinability: ConfluenceJoinabilityType;
	identityLink: string;
	updateSpaceAccessMutation?: (variables: { variables: { spaceKey: string } }) => Promise<any>;
	spaceKey?: string;
	contentId?: string;
	isRequestAccessPending?: boolean;
}

const RESOLVED_SPACE_KEY_FOR_CONTENT_QUERY = graphql`
	query useRestrictedSpaceScreenButtonPropsResolvedSpaceKeyForContentQuery(
		$cloudId: ID!
		$id: ID!
	) {
		confluence_resolvedSpaceKeyForContent(cloudId: $cloudId, id: $id)
	}
`;

export const useButtonProps = ({
	confluenceJoinability,
	identityLink,
	updateSpaceAccessMutation,
	spaceKey,
	contentId,
	isRequestAccessPending,
}: UseButtonParams): UseButtonPropsReturn => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { edition, isLoggedIn, cloudId } = useSessionData();
	const isLicensed = useIsLicensed();
	const lazyResolveSpaceKey = useLazyQueryPromiseInterop<
		useRestrictedSpaceScreenButtonPropsResolvedSpaceKeyForContentQuery$data,
		useRestrictedSpaceScreenButtonPropsResolvedSpaceKeyForContentQuery$variables
	>(RESOLVED_SPACE_KEY_FOR_CONTENT_QUERY);
	const [hasSpaceKeyResolutionFailed, setHasResolutionFailed] = useState(false);

	// All buttons are defined here
	const requestLicenseButtonProps = useMemo<LinkButtonProps>(
		() => ({
			children: (
				<FormattedMessage
					id="no-permission.request.license"
					description="Link to request license"
					defaultMessage="Request full access"
				/>
			),
			href: identityLink,
			target: '_blank',
			rel: 'noopener noreferrer',
			onClick: () => {
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'requestLicense',
						source: 'restrictedSpaceScreen',
					},
				}).fire();
			},
		}),
		[identityLink, createAnalyticsEvent],
	);

	const goHomeButtonProps = useMemo<LinkButtonProps>(
		() => ({
			href: WIKI.toUrl(),
			children: (
				<FormattedMessage
					id="no-permission.restricted.space.licensed-user.request-access.go-home-button"
					defaultMessage="Go home"
					description="button text for licensed users to go back to the home page"
				/>
			),
			onClick: () => {
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						source: 'restrictedSpaceScreen',
						actionSubject: 'button',
						action: 'clicked',
						actionSubjectId: 'goHome',
					},
				}).fire();
			},
		}),
		[createAnalyticsEvent],
	);

	const requestAccessButtonProps = useMemo<ButtonProps>(() => {
		let buttonContent;

		if (isRequestAccessPending) {
			buttonContent = (
				<FormattedMessage
					id="no-permission.restricted.space.licensed-user.request-access.request-access-pending-button"
					defaultMessage="Access requested"
					description="button text for licensed users to show that their request is pending"
				/>
			);
		} else if (fg('access-request-dialog-content-fixes')) {
			buttonContent = (
				<FormattedMessage
					id="no-permission.restricted.space.licensed-user.request-access.ask-for-access-button"
					defaultMessage="Ask for access"
					description="button text to ask for access to a restricted space for licensed users"
				/>
			);
		} else {
			buttonContent = (
				<FormattedMessage
					id="no-permission.restricted.space.licensed-user.request-access.request-access-button"
					defaultMessage="Request space access"
					description="button text to request access to a restricted space for licensed users"
				/>
			);
		}

		return {
			isDisabled: isRequestAccessPending || hasSpaceKeyResolutionFailed,
			children: buttonContent,
			onClick: async () => {
				try {
					if (hasSpaceKeyResolutionFailed) {
						return;
					}

					let resolvedSpaceKey = spaceKey ?? '';

					if (contentId && expVal('confluence_frontend_ssr_space_not_found', 'isEnabled', false)) {
						const result = await lazyResolveSpaceKey({
							variables: {
								cloudId,
								id: contentId,
							},
						});
						resolvedSpaceKey = result?.confluence_resolvedSpaceKeyForContent ?? '';
					}

					if (updateSpaceAccessMutation) {
						await updateSpaceAccessMutation({
							variables: {
								spaceKey: resolvedSpaceKey,
							},
						});
					} else {
						if (!resolvedSpaceKey) {
							throw new Error(`Failed to resolve space key`);
						} else {
							throw new Error('Unexpected error');
						}
					}
				} catch (e) {
					setHasResolutionFailed(true);
					markErrorAsHandled(e);
				}
			},
		};
	}, [
		isRequestAccessPending,
		updateSpaceAccessMutation,
		lazyResolveSpaceKey,
		cloudId,
		contentId,
		hasSpaceKeyResolutionFailed,
		spaceKey,
	]);

	const getButtonProps = useCallback(() => {
		if (confluenceJoinability === 'canJoinDirectly') {
			return {
				primary: requestLicenseButtonProps,
				secondary: goHomeButtonProps,
			};
		} else if (confluenceJoinability === 'licensingInProgress') {
			return {
				primary: goHomeButtonProps,
			};
		} else if (!isLoggedIn || !isLicensed || edition === ConfluenceEdition.FREE) {
			// Only one button for free edition or anonymous users or unlicensed users
			return { primary: goHomeButtonProps };
		} else {
			return {
				primary: requestAccessButtonProps,
				secondary: goHomeButtonProps,
			};
		}
	}, [
		requestLicenseButtonProps,
		goHomeButtonProps,
		requestAccessButtonProps,
		confluenceJoinability,
		edition,
		isLoggedIn,
		isLicensed,
	]);

	return getButtonProps();
};
